<template>
  <div class="ViewerImage">

    <transition name="ViewerImage-image-">

      <img class="ViewerImage-image"
        ref="image"
        v-if="resolvedSrc"
        :src="resolvedSrc" />

    </transition>

    <transition name="ViewerImage-progress-">

      <div class="ViewerImage-progress"
        v-show="showProgressBar"
        :style="progressStyles">
      </div>

    </transition>

  </div>
</template>

<script>
import 'lazysizes/plugins/object-fit/ls.object-fit'
import 'lazysizes/plugins/parent-fit/ls.parent-fit'
import 'lazysizes/plugins/respimg/ls.respimg'
import 'lazysizes'
import axios from 'axios'
import { assign, find } from 'lodash'

import config from 'src/config/smugmug.yaml'

export default {
  name: 'ViewerImage',

  props: {
    load: {
      type: Boolean,
      default: false
    },
    smugmug: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      /**
       * Holds image data
       * @type {Object}
       */
      data: {
        title: '',
        caption: '',
        keywords: [],
        template: '',
        ratio: [],
        bytes: 0
      },

      /**
       * Whether image has been initialized yet
       * @type {Boolean}
       */
      initialized: false,

      /**
       * Whether SmugMug data has been loaded
       * @type {Boolean}
       */
      dataLoaded: false,

      /**
       * Resolved source
       * @type {String}
       */
      resolvedSrc: null,

      isLoading: false,

      /**
       * Width of the current image
       * @type {Number}
       */
      currentSize: 0,

      /**
       * Progress data
       * @type {Object}
       */
      progress: 0,

      showProgressBar: true,

      result: {
        data: null
      }
    }
  },

  computed: {
    window () { return this.$store.state.window },

    /**
     * Styles for the progress bar
     * @return {Object}
     */
    progressStyles () {
      return {
        transform: `scaleX(${this.progress})`
      }
    }
  },

  mounted () {
    if (!this.load || this.initialized) return
    this.initialized = true
    this.$nextTick(this.init)
  },

  watch: {
    load () {
      if (!this.load || this.initialized) return
      this.initialized = true
      this.init()
    },

    dataLoaded () {
      this.emitDataLoadedEvent()
    },

    /**
     * Reload image on window width change
     */
    'window.width': 'chooseImageSize',

    'currentSize': 'loadImage',

    'result.data': 'encodeImageSrc'
  },

  methods: {
    /**
     * Initialize
     */
    async init () {
      this.data = await this.$smugmug.get(this.smugmug)
      this.dataLoaded = true
      this.chooseImageSize()
    },

    /**
     * Load image from proxy server
     *
     * TODO:
     * - When is it necessary to load a new image?
     * - When the requested size is larger than the loaded image.
     */
    chooseImageSize () {
      if (!this.dataLoaded) return

      // Get first image that is >= to element width
      let size = find(config.image_sizes, size => size >= this.$el.clientWidth)

      if (!size) size = config.image_sizes[config.image_sizes.length - 1]

      if (size === this.currentSize) return
      this.currentSize = size
    },

    /**
     * Load image from proxy at requested size
     */
    async loadImage () {
      // Resolve URL template for the current size
      const url = this.$smugmug.resolveTemplate(this.data, this.currentSize)
      const proxiedUrl = config.proxy_url + url

      const data = await axios.get(proxiedUrl, {
        responseType: 'arraybuffer',

        onDownloadProgress: pe => {
          if (pe.loaded >= pe.total) {
            this.progress = 100
            return
          }

          this.progress = pe.loaded / pe.total
        }
      })

      this.result = data
    },

    encodeImageSrc () {
      const arrayBuffer = this.result.data

      if (!arrayBuffer) return

      let byteArray = new Uint8Array(arrayBuffer)
      let i = byteArray.length
      const biStr = new Array(i)

      while (i--) {
        biStr[i] = String.fromCharCode(byteArray[i])
      }

      const data = biStr.join('')
      const base64 = window.btoa(data)

      this.resolvedSrc = `data:image/jpeg;base64,${base64}`
      this.showProgressBar = false
    },

    /**
     * Emit the dataLoaded event
     */
    emitDataLoadedEvent () {
      this.$emit('dataLoaded', assign(this.data, { id: this.smugmug }))
    }
  }
}
</script>

<style>
@import 'properties';

.ViewerImage {
  position: relative;
  display: flex;
  flex-grow: 1;
}

.ViewerImage-image {
  position: relative;
  z-index: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.ViewerImage-image--enter-active {
  transition: opacity 0.3s ease;
}

.ViewerImage-image--enter {
  opacity: 0;
}

.ViewerImage-progress {
  position: absolute;
  z-index: 1;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: var(--c-blue);
  will-change: transform;
  transition: transform 0.2s ease;
  transform-origin: left;
  transform: scaleX(0);
}

.ViewerImage-progress--enter-active,
.ViewerImage-progress--leave-active {
  transition: opacity 0.3s ease;
}

.ViewerImage-progress--enter,
.ViewerImage-progress--leave-to {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// ViewerImage.vue?42d3b900